/**
 * Polish plural forms for nouns.
 * Polish has 3 forms: singular (1), plural for 2-4, and plural for 5+
 *
 * @param count - The number
 * @param singular - Form for 1 (e.g., "nabór")
 * @param plural2to4 - Form for 2-4 (e.g., "nabory")
 * @param plural5plus - Form for 5+ and 0 (e.g., "naborów")
 * @returns The correct form based on the count
 */
export function pluralizePl(
  count: number,
  singular: string,
  plural2to4: string,
  plural5plus: string
): string {
  const absCount = Math.abs(count);

  if (absCount === 1) {
    return singular;
  }

  // For numbers 2-4, 22-24, 32-34, etc. (but not 12-14)
  const lastDigit = absCount % 10;
  const lastTwoDigits = absCount % 100;

  if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14)) {
    return plural2to4;
  }

  return plural5plus;
}

/**
 * Format "Znaleziono X naborów" with correct Polish grammar
 */
export function formatNaboryCount(count: number): string {
  const form = pluralizePl(count, "nabór", "nabory", "naborów");
  return `Znaleziono ${count} ${form}`;
}

/**
 * Map of Polish diacritics to their ASCII equivalents.
 */
const POLISH_DIACRITICS_MAP: Record<string, string> = {
  'ą': 'a',
  'ć': 'c',
  'ę': 'e',
  'ł': 'l',
  'ń': 'n',
  'ó': 'o',
  'ś': 's',
  'ź': 'z',
  'ż': 'z',
};

/**
 * Remove Polish diacritics from text, converting to ASCII equivalents.
 *
 * @param text - Text containing Polish characters
 * @returns Text with Polish diacritics replaced by ASCII equivalents
 * @example
 * removePolishDiacritics("żółć") // returns "zolc"
 * removePolishDiacritics("dolnośląskie") // returns "dolnoslaskie"
 */
export function removePolishDiacritics(text: string): string {
  // Type check and length limit to prevent potential ReDoS attacks
  if (typeof text !== 'string' || text.length > 1000) {
    return typeof text === 'string' ? text : '';
  }
  return text
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (char) => POLISH_DIACRITICS_MAP[char] || char);
}

/**
 * Convert Polish region name to URL-safe slug for API calls.
 *
 * @param region - Polish region name (e.g., "dolnośląskie")
 * @returns URL-safe slug (e.g., "dolnoslaskie")
 * @example
 * toVoivodeshipSlug("dolnośląskie") // returns "dolnoslaskie"
 * toVoivodeshipSlug("łódzkie") // returns "lodzkie"
 */
export function toVoivodeshipSlug(region: string): string {
  return removePolishDiacritics(region);
}

/**
 * Append a canned reason snippet to an existing rejection reason field value.
 *
 * Rules:
 * - Empty current → return snippet alone (no leading separator).
 * - Non-empty current → append "\n" + snippet. If current already ends with "\n",
 *   do not add another one (avoids double blank line).
 * - Idempotence: if current === snippet exactly, return current unchanged (no-op).
 * - Cap: if the resulting string would exceed maxLength, return current unchanged (no-op).
 *   A result equal to exactly maxLength is allowed.
 *
 * @param current - The current value of the textarea.
 * @param snippet - The full text of the canned reason to append.
 * @param maxLength - Maximum allowed character count (inclusive boundary).
 * @returns The new value, or `current` unchanged when the operation is a no-op.
 */
export function appendCannedReason(
  current: string,
  snippet: string,
  maxLength: number
): string {
  // Idempotence: if field already equals the snippet, do nothing
  if (current === snippet) {
    return current;
  }

  const result =
    current === ""
      ? snippet
      : current.endsWith("\n")
        ? current + snippet
        : current + "\n" + snippet;

  // Cap: no-op if result exceeds maxLength
  if (result.length > maxLength) {
    return current;
  }

  return result;
}
